import Menu from 'lucide-react-native/dist/esm/icons/menu.js';
import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Image,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { AccountDropdown } from './AccountDropdown';
import { HeaderSyncIndicator } from './HeaderSyncIndicator';
import { ChevronLeft } from './icons';
import { NavigationDrawer } from './NavigationDrawer';
import { featureFlags } from '../config/featureFlags';
import { useAuth } from '../contexts/AuthContext';
import { ImpersonationBanner } from './impersonation/ImpersonationBanner';
import { useNetworkStatus } from '../hooks/queries/useOfflineRequestsQuery';
import { useResponsive } from '../hooks/useResponsive';
import { roleService } from '../services/auth/roleService';

interface HeaderProps {
  navigate?: (path: string) => void;
  currentPath?: string;
  hideLogo?: boolean;
  title?: string | React.ReactNode;
  rightElement?: React.ReactNode;
  disableMenu?: boolean;
}

const isWeb = Platform.OS === 'web';
export const GLOBAL_HEADER_WEB_HEIGHT = 60;

export const Header: React.FC<HeaderProps> = ({
  navigate,
  currentPath,
  hideLogo = false,
  title,
  rightElement,
  disableMenu = false,
}) => {
  const insets = useSafeAreaInsets();
  const { user } = useAuth();
  const { isSmallScreen } = useResponsive();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isOnline = useNetworkStatus();

  // Check if user can access prompt management (IT Admin only + feature flag)
  const canAccessPromptManagement =
    featureFlags.promptManagement &&
    roleService.canAccessPromptManagement(user);
  // Check if user can access feedback view (IT Admin only)
  const canAccessFeedbackView = roleService.canAccessFeedbackView(user);
  const canAccessRoleManagement = roleService.canAccessRoleManagement(user);

  // Determine active states based on current path
  const isRoleManagementActive =
    currentPath === '/users' || currentPath === '/role-management';
  const isPromptManagementActive = currentPath === '/prompt-management';
  const isFeedbackViewActive = currentPath === '/feedback-view';
  const isViewAllRequestsScreen =
    currentPath === '/request' ||
    currentPath === '/view-requests' ||
    !currentPath ||
    currentPath === '/';
  const showRequestsBackNav = !hideLogo && !isViewAllRequestsScreen;

  const handleHomePress = () => {
    if (navigate) {
      navigate('/request');
    }
  };

  const handleLogoPress = () => {
    if (navigate) {
      navigate('/view-requests');
    }
  };

  const handlePromptManagementPress = () => {
    if (navigate) {
      navigate('/prompt-management');
    }
  };

  const handleFeedbackViewPress = () => {
    if (navigate) {
      navigate('/feedback-view');
    }
  };

  const handleRoleManagementPress = () => {
    if (navigate) {
      navigate('/users');
    }
  };

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  return (
    <>
      <View style={[styles.container, { paddingTop: insets.top }]}>
        <ImpersonationBanner />
        <View style={styles.headerContent}>
          <View style={styles.leftSection}>
            {!hideLogo && (
              <TouchableOpacity
                testID="nav-logo"
                style={styles.logoContainer}
                onPress={handleLogoPress}
              >
                <Image
                  source={require('../assets/Vertiv-Logo.png')}
                  style={styles.logo}
                  resizeMode="contain"
                />
              </TouchableOpacity>
            )}
            {!hideLogo && showRequestsBackNav && (
              <View style={styles.logoDivider} accessibilityElementsHidden />
            )}
            {showRequestsBackNav && (
              <TouchableOpacity
                testID="nav-requests"
                style={[
                  styles.leftNavItem,
                  disableMenu && styles.navItemDisabled,
                ]}
                onPress={handleHomePress}
                disabled={disableMenu}
              >
                <ChevronLeft
                  size={16}
                  color={disableMenu ? '#ccc' : '#ff6b35'}
                  style={styles.leftNavIcon}
                />
                <Text
                  style={
                    disableMenu
                      ? styles.navItemTextDisabled
                      : styles.leftNavItemText
                  }
                >
                  Requests
                </Text>
              </TouchableOpacity>
            )}
            {title &&
              (typeof title === 'string' ? (
                <Text style={styles.titleText}>{title}</Text>
              ) : (
                <View style={styles.titleContainer}>{title}</View>
              ))}
            <HeaderSyncIndicator style={styles.headerSync} />
          </View>

          <View style={[styles.rightSection, isSmallScreen && { gap: 8 }]}>
            {!isOnline && (
              <View style={styles.offlineBadge}>
                <Text style={styles.offlineBadgeText}>Offline</Text>
              </View>
            )}
            {rightElement && (
              <View style={styles.rightElementContainer}>{rightElement}</View>
            )}
            {isSmallScreen ? (
              <TouchableOpacity
                style={[
                  styles.hamburgerButton,
                  disableMenu && styles.hamburgerButtonDisabled,
                ]}
                onPress={toggleDrawer}
                disabled={disableMenu}
              >
                <Menu size={24} color={disableMenu ? '#ccc' : '#666'} />
              </TouchableOpacity>
            ) : (
              <View style={styles.navContainer}>
                {canAccessRoleManagement && (
                  <TouchableOpacity
                    testID="nav-role-management"
                    style={[
                      styles.navItem,
                      disableMenu && styles.navItemDisabled,
                    ]}
                    onPress={handleRoleManagementPress}
                    disabled={disableMenu}
                  >
                    <Text
                      style={
                        disableMenu
                          ? styles.navItemTextDisabled
                          : isRoleManagementActive
                          ? styles.navItemTextActive
                          : styles.navItemText
                      }
                    >
                      Users
                    </Text>
                  </TouchableOpacity>
                )}
                {canAccessPromptManagement && isWeb && (
                  <TouchableOpacity
                    style={[
                      styles.navItem,
                      disableMenu && styles.navItemDisabled,
                    ]}
                    onPress={handlePromptManagementPress}
                    disabled={disableMenu}
                  >
                    <Text
                      style={
                        disableMenu
                          ? styles.navItemTextDisabled
                          : isPromptManagementActive
                          ? styles.navItemTextActive
                          : styles.navItemText
                      }
                    >
                      Prompt Management
                    </Text>
                  </TouchableOpacity>
                )}
                {/* Feedback hidden from nav — canAccessFeedbackView kept for future re-enable */}
                {false && canAccessFeedbackView && isWeb && (
                  <TouchableOpacity
                    testID="nav-feedback"
                    style={[
                      styles.navItem,
                      disableMenu && styles.navItemDisabled,
                    ]}
                    onPress={handleFeedbackViewPress}
                    disabled={disableMenu}
                  >
                    <Text
                      style={
                        disableMenu
                          ? styles.navItemTextDisabled
                          : isFeedbackViewActive
                          ? styles.navItemTextActive
                          : styles.navItemText
                      }
                    >
                      Feedback
                    </Text>
                  </TouchableOpacity>
                )}
                <AccountDropdown
                  hideSettingsText={true}
                  noBorder={true}
                  navigate={navigate}
                  disabled={disableMenu}
                />
              </View>
            )}
          </View>
        </View>
      </View>

      <NavigationDrawer
        isVisible={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        navigate={navigate}
        currentPath={currentPath}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    width: '100%',
    alignItems: isWeb ? 'center' : 'stretch',
    ...Platform.select({
      web: {
        position: 'sticky' as any,
        top: 0,
        zIndex: 1000,
        shadowColor: '#000',
        shadowOffset: {
          width: 0,
          height: 2,
        },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
      },
    }),
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: Platform.OS !== 'web' ? 8 : 24, // Further reduce left padding on mobile
    paddingVertical: Platform.OS !== 'web' ? 12 : 0, // Only change mobile
    minHeight: GLOBAL_HEADER_WEB_HEIGHT,
    width: '100%',
    alignSelf: 'stretch',
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Platform.OS !== 'web' ? 8 : 12, // Only change mobile
    flex: Platform.OS !== 'web' ? 1 : undefined, // Only add flex on mobile
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  logo: {
    width: Platform.OS !== 'web' ? 90 : 100, // Only change mobile
    height: Platform.OS !== 'web' ? 28 : 32, // Only change mobile
  },
  logoDivider: {
    width: 1,
    height: 24,
    backgroundColor: '#e9ecef',
    marginHorizontal: 12,
  },
  leftNavItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 8,
    paddingHorizontal: 4,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  leftNavIcon: {
    marginTop: 1,
  },
  leftNavItemText: {
    fontSize: 14,
    color: '#ff6b35',
    fontWeight: '700',
  },
  hamburgerButton: {
    padding: Platform.OS !== 'web' ? 6 : 8, // Only change mobile
    alignItems: Platform.OS !== 'web' ? 'center' : undefined, // Only add on mobile
    justifyContent: Platform.OS !== 'web' ? 'center' : undefined, // Only add on mobile
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  hamburgerButtonDisabled: {
    opacity: 0.5,
    ...Platform.select({
      web: {
        cursor: 'not-allowed',
      },
    }),
  },
  headerSync: {
    // Styling for the sync indicator in the header
  },
  navContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: 4,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  navItemText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  navItemTextAdmin: {
    fontSize: 14,
    color: '#ff6b35',
    fontWeight: '500',
  },
  navItemTextActive: {
    fontSize: 14,
    color: '#ff6b35',
    fontWeight: '500',
  },
  navItemDisabled: {
    opacity: 0.5,
    ...Platform.select({
      web: {
        cursor: 'not-allowed',
      },
    }),
  },
  navItemTextDisabled: {
    fontSize: 14,
    color: '#ccc',
    fontWeight: '500',
  },
  titleText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ff6b35',
    marginLeft: 8,
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginLeft: 8,
    justifyContent: 'flex-start',
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  rightElementContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  offlineBadge: {
    backgroundColor: '#dc3545',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  offlineBadgeText: {
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: 12,
  },
});
